/**
 * Which day card is owed focus right now — the dock's answer and the move's, merged into the one
 * token the columns already read.
 *
 * Two different acts leave focus on `<body>`, and both do it for the same reason: the element the
 * browser would hand focus back to has been unmounted by the act itself.
 *
 * · **Closing the dock** unmounts nothing, but under the modal the week behind it is `aria-hidden`
 *   at the moment Base UI restores, so `useDayDock` names the day instead and the card claims focus
 *   when it can. That mechanism is older than this file and unchanged.
 * · **Committing a day MOVE** re-renders the card into another column: the lifted instance unmounts
 *   and the new one mounts a metre away. Both non-pointer paths end there — the keyboard lift's
 *   Space, and the menu's weekday entry whose trigger sits inside the card that has just gone.
 *
 * The card already knows how to take focus from a token it recognises, so the move reuses that
 * rather than growing a second mechanism: the canvas merges the two signals here and hands one
 * token down. The DOCK wins where both are set, because closing the dock is the later act.
 *
 * ── The move's token is a ONE-SHOT, and that is what this hook is for ────────────────────────
 * Left standing it would be claimed again by every later re-mount of that card — a phase switch,
 * a canvas swap — and a trainer reading somewhere else would be yanked back to a workout they moved
 * ten minutes ago. Clearing it in a parent effect is what makes it fire exactly once: React runs a
 * child's effects before its parent's, so the card has already taken the focus by the time this
 * runs.
 */

import { useEffect } from 'react';

export interface MoveFocusSignal {
  readonly focusToken: string | null;
  readonly focusTaken: () => void;
}

export function useMovedCardFocus(
  dockToken: string | null,
  move: MoveFocusSignal,
): string | null {
  const { focusToken, focusTaken } = move;
  useEffect(() => {
    if (focusToken === null) return;
    focusTaken();
  }, [focusToken, focusTaken]);
  return dockToken ?? focusToken;
}
